<script setup lang="ts">
import { computed, toRefs } from 'vue';
import AdpDocumentCardLink from '@/components/common/adp-document/adp-document-card-link.vue';
import DocumentBasicInfoLayout from '@/components/common/adp-document/basic-info/document-basic-info-layout.vue';
import DocumentMediaName from '@/components/common/adp-document/basic-info/document-media-name.vue';
import { ResearchPaper } from '@/types';

interface Props {
  adpDocument: ResearchPaper;
  size?: 'xm' | 'm' | 'l';
  imageVariant?: 'banner' | 'thumbnail';
  isNarrow?: boolean;
  isRelatedArticle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'xm',
  imageVariant: 'thumbnail',
  isNarrow: false,
  isRelatedArticle: false,
});

const emit = defineEmits<{
  'article-link-clicked': [clickedMainTitle?: boolean];
  'article-link-context-menu': [event: Event];
}>();

const { adpDocument } = toRefs(props);

const isPdf = computed(() => !!props.adpDocument.pdf_url);

const onProvidedSiteClicked = () => {
  adpDocument.value.is_read = true;
  emit('article-link-clicked');
};
</script>

<template>
  <DocumentBasicInfoLayout
    :adp-document="adpDocument"
    :size="size"
    :image-variant="imageVariant"
    :is-narrow="isNarrow"
    :is-related-article="isRelatedArticle"
    @article-link-clicked="emit('article-link-clicked', $event)"
    @article-link-context-menu="emit('article-link-context-menu', $event)"
  >
    <template #publisher>
      <DocumentMediaName :publisher="adpDocument.journal_name">
        <template #trailing>
          <VTooltip
            placement="bottom"
            :disabled="!adpDocument.is_priority_journal"
          >
            <span
              v-if="adpDocument.is_priority_journal"
              class="priority-journal c-text-regular--xs"
              >SJR</span
            >
            <template #popper>
              <div>
                <a
                  class="help-link"
                  href="https://anews-stockmark.zendesk.com/hc/ja/articles/13717202977689-%E8%AB%96%E6%96%87%E6%A9%9F%E8%83%BD%E3%81%AB%E3%81%A4%E3%81%84%E3%81%A6"
                  target="_blank"
                  rel="noopener noreferrer"
                  >SJRスコア</a
                >の上位に入る影響度の高い学術雑誌です
              </div>
            </template>
          </VTooltip>
        </template>
      </DocumentMediaName>
    </template>
    <template #afterTitle>
      <div v-if="isPdf" class="document-original-site c-text-regular--s">
        <AdpDocumentCardLink
          :adp-document="adpDocument"
          :use-pdf-url="false"
          @article-link-clicked="onProvidedSiteClicked"
          @article-link-context-menu="emit('article-link-context-menu', $event)"
        >
          提供サイトで見る
        </AdpDocumentCardLink>
      </div>
    </template>
  </DocumentBasicInfoLayout>
</template>

<style lang="scss" scoped>
.priority-journal {
  align-self: center;
  height: 16px;
  margin-left: 2px;
  padding: 0 2px;
  color: #fff;
  background: $color-priority-journal;
}

.document-original-site {
  color: $color-gray800;
  text-decoration: underline;
  margin: 4px 0;
}
</style>
